import { ThemeEvents } from '@theme/events';

/**
 * "Added to cart" notification for the product card Add button (_product-card-add-button).
 *
 * Product lists are long on phones and the cart icon is far away, so a successful add
 * shows a short toast with the product name and a "View cart" link that opens the cart
 * drawer. It is visual only: the product form's own live region already announces
 * "Added" to screen readers. Skipped when the cart drawer opens by itself after an add.
 */

const DISPLAY_MS = 4000;

/** @type {HTMLElement | null} */
let toast = null;

/** @type {number | undefined} */
let hideTimeout;

/**
 * @param {HTMLElement} source - The card's .wyn-card-add wrapper, which carries the copy
 * @returns {HTMLElement}
 */
function getToast(source) {
  if (toast?.isConnected) return toast;

  toast = document.createElement('div');
  toast.className = 'wyn-add-toast';
  toast.hidden = true;
  toast.innerHTML = `
    <span class="wyn-add-toast__icon" aria-hidden="true"></span>
    <span class="wyn-add-toast__text">
      <strong class="wyn-add-toast__title"></strong>
      <span class="wyn-add-toast__product"></span>
    </span>
    <a class="wyn-add-toast__action"></a>
  `;

  const action = /** @type {HTMLAnchorElement} */ (toast.querySelector('.wyn-add-toast__action'));
  action.href = source.dataset.cartUrl || '/cart';
  action.addEventListener('click', (event) => {
    const drawer = /** @type {(HTMLElement & { open?: () => void }) | null} */ (
      document.querySelector('cart-drawer-component')
    );
    if (typeof drawer?.open !== 'function') return;

    event.preventDefault();
    hide();
    drawer.open();
  });

  // Keep it on screen while the shopper is reading or reaching for it.
  toast.addEventListener('pointerenter', () => window.clearTimeout(hideTimeout));
  toast.addEventListener('pointerleave', scheduleHide);
  toast.addEventListener('focusin', () => window.clearTimeout(hideTimeout));
  toast.addEventListener('focusout', scheduleHide);

  document.body.append(toast);
  return toast;
}

function hide() {
  window.clearTimeout(hideTimeout);
  toast?.removeAttribute('data-open');
}

function scheduleHide() {
  window.clearTimeout(hideTimeout);
  hideTimeout = window.setTimeout(hide, DISPLAY_MS);
}

/** @param {HTMLElement} source */
function show(source) {
  const element = getToast(source);
  const title = element.querySelector('.wyn-add-toast__title');
  const product = element.querySelector('.wyn-add-toast__product');
  const action = element.querySelector('.wyn-add-toast__action');

  if (title) title.textContent = source.dataset.toastTitle ?? '';
  if (product) product.textContent = source.dataset.productTitle ?? '';
  if (action) action.textContent = source.dataset.toastAction ?? '';

  // No forced style flush here (it re-styles the whole page mid-tap): @starting-style in
  // the card add button's CSS gives the first appearance its slide-in.
  element.hidden = false;
  element.setAttribute('data-open', '');
  scheduleHide();
}

// Modules run once per page, so this listener is registered once however many cards
// load the script.
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const source = event.target instanceof Element ? event.target.closest('.wyn-card-add') : null;
  if (!(source instanceof HTMLElement)) return;

  const { detail } = /** @type {import('@theme/events').CartAddEvent} */ (event);
  if (detail?.data?.didError || source.dataset.cartAutoOpen === 'true') return;

  show(source);
});
